import React from 'react';

import { ActionIcon, Box, Button, Flex, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { DroxyEmptyStateBox } from 'components/Droxy/DroxyEmptyStateBox';
import { useKeyValueEditor } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/KeyValueEditor/hooks/useKeyValueEditor';
import { VariableInsertButton } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/VariableInsertButton';
import { useTranslation } from 'react-i18next';

interface KeyValueEditorProps {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  label: string;
  placeholder: string;
  keyPlaceholder: string;
  valuePlaceholder: string;
  variableJsonSchema?: string;
}

export function KeyValueEditor({
  value,
  onChange,
  label,
  placeholder,
  keyPlaceholder,
  valuePlaceholder,
  variableJsonSchema
}: KeyValueEditorProps) {
  const { t } = useTranslation();
  const { entries, addItem, removeItem, updateKey, updateValue } = useKeyValueEditor({
    value,
    onChange
  });

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="md">
        <Text size="sm" fw={500} c="dark">
          {label}
        </Text>
        {entries.length > 0 && (
          <Button variant="outline" leftIcon={<IconPlus size={16} />} onClick={addItem} size="sm">
            {t('KEY_VALUE_EDITOR.ADD_ITEM')}
          </Button>
        )}
      </Flex>

      {entries.length === 0 ? (
        <DroxyEmptyStateBox message={placeholder}>
          <Button variant="filled" onClick={addItem} size="sm">
            {t('KEY_VALUE_EDITOR.ADD_YOUR_FIRST_ITEM')}
          </Button>
        </DroxyEmptyStateBox>
      ) : (
        <Stack spacing="xs">
          {entries.map(([key, itemValue], keyIndex) => (
            <Flex key={`${key}-${keyIndex}`} gap="xs">
              <TextInput
                placeholder={keyPlaceholder}
                defaultValue={key}
                onBlur={(event) => updateKey(key, event.target.value)}
                style={{ flex: 1 }}
              />
              <TextInput
                placeholder={valuePlaceholder}
                value={itemValue}
                onChange={(e) => updateValue(key, e.target.value)}
                style={{ flex: 2 }}
                rightSection={
                  variableJsonSchema ? (
                    <VariableInsertButton
                      jsonSchema={variableJsonSchema}
                      onInsertVariable={(variable) => {
                        const currentValue = value[key] || '';
                        updateValue(key, currentValue + `{{${variable}}}`);
                      }}
                    />
                  ) : undefined
                }
                rightSectionProps={{ style: { pointerEvents: 'all' } }}
              />
              <ActionIcon size="md" variant="subtle" color="red" onClick={() => removeItem(key)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Flex>
          ))}
        </Stack>
      )}
    </Box>
  );
}
